import apiClient from "../api-client.js";

/**
 * Clientify endpoints, mounted under `/integrations/clientify/`.
 *
 * The connection is personal: a superadmin links each `agency_admin` /
 * `consultant` to their own Clientify user, and from then on every list is
 * filtered to what that Clientify user owns. Everything is read and written
 * live in Clientify, nothing is cached on our side.
 */

const BASE = "/integrations/clientify";
const CONNECTIONS = `${BASE}/connections`;
const DEALS = `${BASE}/deals`;
const CONTACTS = `${BASE}/contacts`;

export const CLIENTIFY_DEALS_ENDPOINT = `${DEALS}/`;
export const CLIENTIFY_CONTACTS_ENDPOINT = `${CONTACTS}/`;
export const CLIENTIFY_STAGES_ENDPOINT = `${DEALS}/stages/`;

export const DEAL_STATUS = { open: "1", expired: "2", won: "3", lost: "4" };

export const CONTACT_STATUS_OPTIONS = [
  { value: "cold-lead", label: "Lead frío" },
  { value: "warm-lead", label: "Lead templado" },
  { value: "hot-lead", label: "Lead caliente" },
  { value: "in-deal", label: "En oportunidad" },
  { value: "client", label: "Cliente" },
  { value: "lost-lead", label: "Lead perdido" },
  { value: "lost-client", label: "Cliente perdido" },
  { value: "not-qualified-lead", label: "Lead no calificado" },
  { value: "visitor", label: "Visitante" },
  { value: "other", label: "Otro" },
];

/** First readable message of a DRF error, field errors included. */
export const clientifyErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data?.detail === "string") return data.detail;
  if (data && typeof data === "object") {
    const first = Object.values(data)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
    if (typeof first === "string") return first;
  }
  return fallback;
};

/* ------------------------------------------------------------------ */
/* Connections (superadmin)                                            */
/* ------------------------------------------------------------------ */

export const listClientifyConnections = async () => {
  const response = await apiClient.get(`${CONNECTIONS}/`);
  return response.data || [];
};

/** Clientify users, each flagged with `connected` / `connected_user_email`. */
export const listClientifyUsers = async () => {
  const response = await apiClient.get(`${CONNECTIONS}/clientify-users/`);
  return response.data?.users || [];
};

export const listClientifyConnectableUsers = async () => {
  const response = await apiClient.get(`${CONNECTIONS}/connectable-users/`);
  return response.data?.users || [];
};

/** `{ user, clientify_user_id }` */
export const connectClientifyUser = async (payload) => {
  const response = await apiClient.post(`${CONNECTIONS}/`, payload);
  return response.data;
};

/** Addressed by the Markezy user id: the connection has no id of its own. */
export const disconnectClientifyUser = async (userId) => {
  await apiClient.delete(`${CONNECTIONS}/${userId}/`);
};

/**
 * Connected users a lead can be assigned to: the caller's agency, or every
 * connected user for a superadmin (optionally narrowed by agency).
 */
export const listClientifyOwners = async (agencyId) => {
  const response = await apiClient.get(`${CONNECTIONS}/owners/`, {
    params: agencyId ? { agency: agencyId } : undefined,
  });
  return response.data || [];
};

/* ------------------------------------------------------------------ */
/* Deals                                                               */
/* ------------------------------------------------------------------ */

export const getClientifyDeal = async (dealId) => {
  const response = await apiClient.get(`${DEALS}/${dealId}/`);
  return response.data;
};

export const createClientifyDeal = async (payload) => {
  const response = await apiClient.post(`${DEALS}/`, payload);
  return response.data;
};

/** Partial update; `{ pipeline_stage_id }` alone is the kanban drop. */
export const updateClientifyDeal = async (dealId, payload) => {
  const response = await apiClient.patch(`${DEALS}/${dealId}/`, payload);
  return response.data;
};

/** `{ result: "won" | "lost", lost_reason?, comment?, closed_date?, amount? }` */
export const closeClientifyDeal = async (dealId, payload) => {
  const response = await apiClient.post(`${DEALS}/${dealId}/close/`, payload);
  return response.data;
};

/** A page of the deal's timeline (Clientify's "Todos"), newest first. */
export const listClientifyDealActivity = async (dealId, page = 1) => {
  const response = await apiClient.get(`${DEALS}/${dealId}/activity/`, {
    params: { page, page_size: 50 },
  });
  return response.data;
};

/** `{ name?, comment }` */
export const addClientifyDealNote = async (dealId, payload) => {
  await apiClient.post(`${DEALS}/${dealId}/notes/`, payload);
};

export const deleteClientifyDeal = async (dealId) => {
  await apiClient.delete(`${DEALS}/${dealId}/`);
};

export const listClientifyLostReasons = async () => {
  const response = await apiClient.get(`${DEALS}/lost-reasons/`);
  return response.data || [];
};

/* ------------------------------------------------------------------ */
/* Contacts                                                            */
/* ------------------------------------------------------------------ */

export const searchClientifyContacts = async (search, ownerId) => {
  const response = await apiClient.get(`${CONTACTS}/`, {
    params: {
      search: search || undefined,
      page_size: 20,
      owner_id: ownerId || undefined,
    },
  });
  return response.data?.results || [];
};

export const getClientifyContact = async (contactId) => {
  const response = await apiClient.get(`${CONTACTS}/${contactId}/`);
  return response.data;
};

export const createClientifyContact = async (payload) => {
  const response = await apiClient.post(`${CONTACTS}/`, payload);
  return response.data;
};

export const updateClientifyContact = async (contactId, payload) => {
  const response = await apiClient.patch(`${CONTACTS}/${contactId}/`, payload);
  return response.data;
};

export const deleteClientifyContact = async (contactId) => {
  await apiClient.delete(`${CONTACTS}/${contactId}/`);
};
